import { useCallback, useEffect } from 'react';

import { numbersApi, PhoneNumber } from 'apis/numbers';
import { actions } from 'components/telephony/actions/telephony';
import {
  FromNumberInitializationStatus,
  fromNumberInitializationStatus,
} from 'components/telephony/constants/fromNumberInitialization';
import { INITIAL_OWN_NUMBERS_LIMIT } from 'components/telephony/constants/ownNumbers';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

export function useInitializeTelephonyFromNumber(): void {
  const dispatch = useAppDispatch();

  const currentlySelectedCallFromNumber = useAppSelector(
    (state) => state.telephony.selectedFromNumber,
  );
  const currentInitializationStatus = useAppSelector(
    (state) => state.telephony.fromNumberInitializationStatus,
  );

  // The first page of owned numbers is loaded eagerly to populate the topbar
  // outbound-number dropdown (`OutboundNumberSelector`). It is no longer used
  // to pick the initial from-number — the backend's `withFallback=true`
  // response below already includes the "first outbound-capable number"
  // fallback, so the client doesn't need to filter and pick locally.
  const { firstPageOwnedNumbersResponse } = numbersApi.useReadNumbersQuery(
    { limit: INITIAL_OWN_NUMBERS_LIMIT },
    {
      selectFromResult: ({ data }) => ({
        firstPageOwnedNumbersResponse: data,
      }),
    },
  );

  // `withFallback=true` makes the backend run the full chain:
  // agent default → org default → first outbound-capable number.
  // It returns `{}` (no `e164_format`) only when the org has no outbound-
  // capable number at all. Guard against partial payloads by requiring
  // `e164_format` before treating the response as usable.
  const {
    data: backendDefaultFromNumberResponse,
    isLoading: isBackendDefaultFromNumberLoading,
    isFetching: isBackendDefaultFromNumberFetching,
  } = numbersApi.useReadDefaultNumberQuery({ withFallback: true });

  const backendDefaultFromNumber = backendDefaultFromNumberResponse?.data
    ?.e164_format
    ? backendDefaultFromNumberResponse.data
    : null;

  const selectFromNumber = useCallback(
    (fromNumber: PhoneNumber | null = null) => {
      if (fromNumber === null) {
        return;
      }

      dispatch(actions.telephonyChangeFromNumber({ number: fromNumber }));
      dispatch(
        actions.telephonySetFromNumberInitializationStatus({
          status: fromNumberInitializationStatus.resolved,
        }),
      );
    },
    [dispatch],
  );

  const setStatusIfChanged = useCallback(
    (nextStatus: FromNumberInitializationStatus) => {
      if (currentInitializationStatus === nextStatus) return;
      dispatch(
        actions.telephonySetFromNumberInitializationStatus({
          status: nextStatus,
        }),
      );
    },
    [currentInitializationStatus, dispatch],
  );

  useEffect(() => {
    if (firstPageOwnedNumbersResponse) {
      dispatch(
        actions.telephonyGetAllOwnNumbersSucceeded(
          firstPageOwnedNumbersResponse,
        ),
      );
    }
  }, [firstPageOwnedNumbersResponse, dispatch]);

  useEffect(
    function resolveAndSelectInitialFromNumber() {
      if (currentlySelectedCallFromNumber !== null) {
        setStatusIfChanged(fromNumberInitializationStatus.resolved);
        return;
      }

      if (
        isBackendDefaultFromNumberLoading ||
        isBackendDefaultFromNumberFetching
      ) {
        setStatusIfChanged(fromNumberInitializationStatus.loading);
        return;
      }

      if (backendDefaultFromNumber === null) {
        setStatusIfChanged(fromNumberInitializationStatus.missing);
        return;
      }

      selectFromNumber(backendDefaultFromNumber);
    },
    [
      currentlySelectedCallFromNumber,
      backendDefaultFromNumber,
      isBackendDefaultFromNumberFetching,
      isBackendDefaultFromNumberLoading,
      selectFromNumber,
      setStatusIfChanged,
    ],
  );
}
